import { GeometryDto, PositionDto, RouteDto, TicketDto } from '@trucktrax/trucktrax-ts-common';
import { Dispatch } from 'redux';

import {
  CLEAN_ROUTE_HISTORY_LIST,
  CLEAN_ROUTE_HISTORY_PRODUCT_LINES,
  CLEAN_SELECTED_ROUTE_HISTORY_LIST,
  SET_ROUTE_HISTORY_HOVER_SELECTION,
  SET_ROUTE_HISTORY_LIST,
  SET_SELECTED_ROUTE_HISTORY_LIST,
} from '../constants/actionConstants';
import { getGeotraxBaseUrl } from '../util/apiUtil';
import { getIdFromUrl } from '../util/appUtil';
import DateTimeRange from '../util/DateTimeRange';
import { devErrorAndLog } from '../util/errorUtil';
import { getRequest } from './requestService';
import { getTicketsByOrderId } from './ticketsService';

export function selectRouteHistoryList(routes: number[] | undefined) {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: SET_SELECTED_ROUTE_HISTORY_LIST,
      payload: routes,
    });
  };
}

export function setRouteHistoryHoverSelection(route: RouteDto | null) {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: SET_ROUTE_HISTORY_HOVER_SELECTION,
      payload: route,
    });
  };
}

/** clears the route history list AND selected routes */
export function clearRouteHistoryList() {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: CLEAN_ROUTE_HISTORY_LIST,
    });
    dispatch({
      type: CLEAN_SELECTED_ROUTE_HISTORY_LIST,
    });
    dispatch({
      type: CLEAN_ROUTE_HISTORY_PRODUCT_LINES,
    });
  };
}

type FetchRouteHistoryListArgs = {
  regionUrl: string;
  timespan: DateTimeRange;
  productLines: any[];
  singleTicketUrl: string;
  orderUrl: string;
  truckUrl: string;
  driverUrl: string;
  plantUrl: string;
  hideDeactivatedTrucks: boolean;
  hideDeactivatedDrivers: boolean;
  zone?: GeometryDto;
  radius?: number;
  latitude?: number;
  longitude?: number;
};

export function fetchRouteHistoryList(args: Partial<FetchRouteHistoryListArgs>) {
  args.productLines ??= [];
  args.singleTicketUrl ??= '';
  args.orderUrl ??= '';
  args.truckUrl ??= '';
  args.driverUrl ??= '';
  args.plantUrl ??= '';

  // if the caller passed only lat/lng/radius (pin) but no zone, build a
  // simple circular geometry object so the backend query behaves consistently.
  // the API expects a GeometryDto (circle or polygon) rather than a full
  // GeoZoneDto wrapper, which is why we avoided the outer `zone` property.
  if (!args.zone && args.latitude != null && args.longitude != null && args.radius != null) {
    args.zone = {
      circle: {
        center: { latitude: args.latitude, longitude: args.longitude },
        radius: args.radius,
      },
    };
  }

  return async (dispatch: Dispatch) => {
    // GET route history
    const url = `${getGeotraxBaseUrl()}/routehistory`;

    const baseparams = {
      regionUrl: args.regionUrl,
      timestampAfter: args.timespan!.start.toUTCString(),
      timestampBefore: args.timespan!.end.toUTCString(),
      productLines: args.productLines,
      ticketUrl: [args.singleTicketUrl],
      trackedObjectUrl: args.truckUrl,
      driverUrl: args.driverUrl,
      plantUrl: args.plantUrl,
      hideDeactivatedTrucks: args.hideDeactivatedTrucks,
      hideDeactivatedDrivers: args.hideDeactivatedDrivers,
      // always serialize the provided geometry object; backend understands circles
      // (it will happily ignore a polygon or circle property)
      geozone: args.zone ? JSON.stringify(args.zone) : '',
    };

    // circle is either passed in as radius value from drawing on the map
    // or it's taken from the radius of the geozone circle if it isn't a polygon
    if (args.zone?.circle) {
      args.radius = args.zone?.circle?.radius;
      args.latitude = args.zone?.circle?.center?.latitude;
      args.longitude = args.zone?.circle?.center?.longitude;
    }

    const params = args.latitude
      ? {
          ...baseparams,
          latitude: args.latitude,
          longitude: args.longitude,
          radius: args.radius,
        }
      : baseparams;

    try {
      if (args.orderUrl) {
        const orderId = getIdFromUrl(args.orderUrl);
        const tickets = (await getTicketsByOrderId(orderId)) as TicketDto[];
        params.ticketUrl = tickets.map(t => t.url!);
      }

      const response = await getRequest(url, params);
      const routes = response.data as RouteDto[];

      if (routes.length > 1) {
        clearRouteHistoryList();
      }

      // map the ticket and order info on each route to all the positions of the route for use on hover/click on the map
      // doing this here because ts-common PositionDto contains order and ticket Dtos
      // which the library-common PositionDto version doesn't have to minimize transfer bulk
      routes.forEach(route => {
        if (route.ticket && route.order) {
          const { ticket, order } = route;
          (route.route as PositionDto[]).forEach(position => {
            position.ticket = ticket;
            position.order = order;
          });
        }
      });

      dispatch({
        type: SET_SELECTED_ROUTE_HISTORY_LIST,
        payload: routes.map(r => r.id),
      });

      dispatch({
        type: SET_ROUTE_HISTORY_LIST,
        payload: routes,
      });
    } catch (e: any) {
      devErrorAndLog(
        'Unable to retrieve routeHistory',
        `geolocationsService: url: ${url} + params: + ${JSON.stringify(params)}`,
        e.toString(),
      );
    }
  };
}
